import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Map, Layers, Layout, Compass, Activity, Brain, CheckCircle2, ArrowRight } from 'lucide-react';

const StudyPaths: React.FC = () => {
    const navigate = useNavigate();

    const handleGeneratePath = () => {
        navigate('/eligibility?service=english-prep');
    };

    return (
        <div className="bg-white min-h-screen pt-32 pb-20 font-sans">
            {/* Hero Header */}
            <div className="bg-[#011E26] text-white py-24 mb-16 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1/3 h-full overflow-hidden opacity-20 hidden lg:block">
                    <div className="w-full h-full bg-gradient-to-l from-[#009B8F] to-transparent"></div>
                    <Compass className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 text-white" />
                </div>

                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="p-3 bg-[#009B8F] rounded-2xl shadow-lg">
                            <Brain className="w-8 h-8 text-[#011E26]" />
                        </div>
                        <span className="text-[#009B8F] font-bold tracking-widest uppercase text-sm">Adaptive Intelligence</span>
                    </div>
                    <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-[1.1]">
                        Automated <br /> <span className="text-[#009B8F]">Study Paths</span>
                    </h1>
                    <p className="text-xl md:text-2xl text-slate-400 max-w-2xl font-light leading-relaxed">
                        Traditional study plans are rigid and generic. Ours adapt as you learn, ensuring you're
                        learning at the perfect level every single day.
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6">
                {/* Value Prop Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-32 items-center">
                    <div className="bg-slate-50 p-12 rounded-[3rem] border border-slate-100 shadow-xl">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#011E26] mb-8 leading-tight">
                            A Smarter, <span className="text-[#009B8F]">Adaptive</span> Learning Journey
                        </h2>
                        <div className="space-y-6 text-slate-600 text-lg">
                            <p>
                                Powered by your AI English Diagnostic results, the system builds a personalised curriculum
                                that refines your path in real time based on your progress.
                            </p>
                            <div className="flex flex-col gap-6 pt-6">
                                <div className="flex gap-4 items-start">
                                    <div className="bg-white p-3 rounded-xl shadow-sm"><Activity className="w-6 h-6 text-[#009B8F]" /></div>
                                    <div>
                                        <h4 className="font-bold text-[#011E26]">Dynamic Support</h4>
                                        <p className="text-sm">Need more time? The system slows down to reinforce fundamentals.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4 items-start">
                                    <div className="bg-white p-3 rounded-xl shadow-sm"><Layers className="w-6 h-6 text-[#009B8F]" /></div>
                                    <div>
                                        <h4 className="font-bold text-[#011E26]">Accelerated Growth</h4>
                                        <p className="text-sm">Progressing fast? Unlock advanced modules and move ahead quickly.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="relative h-full">
                        <img
                            src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=2070&auto=format&fit=crop"
                            alt="Digital Learning Platform"
                            className="rounded-[3rem] shadow-2xl h-full object-cover"
                        />
                    </div>
                </div>

                {/* Three Stage Framework */}
                <div className="mb-32">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-bold text-[#011E26] mb-6">A Three-Stage Framework for Mastery</h2>
                        <p className="text-slate-500 text-lg">Our structured progression mirrors how real language mastery develops.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                stage: "Stage 01",
                                title: "Foundation & Grammar",
                                desc: "Identify recurring grammar issues and build a solid linguistic base before moving forward.",
                                color: "bg-slate-900"
                            },
                            {
                                stage: "Stage 02",
                                title: "Integrated Skill",
                                desc: "Listening, Reading, and Writing modules tailored to your level with guided complex prompts.",
                                color: "bg-[#011E26]"
                            },
                            {
                                stage: "Stage 03",
                                title: "Exam-Style Practice",
                                desc: "Transition into real-time exam practice with timed papers and instant AI grading.",
                                color: "bg-[#009B8F]"
                            }
                        ].map((card, i) => (
                            <div key={i} className={`${card.color} text-white p-12 rounded-[2.5rem] shadow-2xl transition-all hover:scale-105 duration-300`}>
                                <span className="text-xs font-bold tracking-[0.2em] uppercase opacity-50 mb-4 block">{card.stage}</span>
                                <h3 className="text-2xl font-bold mb-6">{card.title}</h3>
                                <p className="text-white/70 leading-relaxed mb-8">{card.desc}</p>
                                <div className="w-10 h-1 border-t-2 border-white/20"></div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Track Your Progress */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-32">
                    <div className="order-2 lg:order-1">
                        <div className="grid grid-cols-2 gap-6">
                            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100">
                                <Layout className="w-10 h-10 text-[#009B8F] mb-4" />
                                <h4 className="font-bold text-[#011E26] mb-2">Visual Growth</h4>
                                <p className="text-sm text-slate-500">Dynamic map of your growth trends and mastered skills.</p>
                            </div>
                            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100">
                                <Map className="w-10 h-10 text-[#009B8F] mb-4" />
                                <h4 className="font-bold text-[#011E26] mb-2">Target Scores</h4>
                                <p className="text-sm text-slate-500">Calculate exactly how close you are to your exam goal.</p>
                            </div>
                        </div>
                    </div>
                    <div className="order-1 lg:order-2">
                        <h2 className="text-4xl font-bold text-[#011E26] mb-8">Track Progress with Intelligent Analytics</h2>
                        <p className="text-slate-600 text-lg mb-8 leading-relaxed">
                            Your dashboard gives you a clear, visual overview of your journey. Instead of a static
                            syllabus, you see a dynamic map of your growth—what you've mastered, what's left,
                            and how close you are to your target score.
                        </p>
                        <ul className="space-y-4">
                            {["Skill strengths and trends", "Estimated exam-ready timeline", "Digital milestones and rewards"].map((item, i) => (
                                <li key={i} className="flex items-center gap-3 text-[#011E26] font-medium">
                                    <CheckCircle2 className="w-5 h-5 text-[#009B8F]" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Final CTA */}
                <div className="bg-[#009B8F] rounded-[4rem] p-16 md:p-24 text-center shadow-2xl relative overflow-hidden group">
                    <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="relative z-10">
                        <h2 className="text-4xl md:text-6xl font-black text-white mb-8 tracking-tight">Start Your Personalised <br /> Journey Today</h2>
                        <p className="text-white/80 text-xl mb-12 max-w-xl mx-auto">
                            Let your study plan adapt to you, not the other way around. Generate your roadmap in seconds.
                        </p>
                        <button
                            onClick={handleGeneratePath}
                            className="bg-[#011E26] text-white px-12 py-6 rounded-3xl font-bold text-xl hover:bg-white hover:text-[#011E26] transition-all flex items-center gap-4 mx-auto shadow-2xl"
                        >
                            Generate My Study Path
                            <ArrowRight className="w-6 h-6" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudyPaths;
